import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemRoundedCheckbox } from "../../UIToolkit/Clickable/SystemRoundedCheckbox";
import { SystemValidationTextField } from "../../UIToolkit/Inputs/SystemValidationTextField";

/** The SMS a holiday popup can send. Off by default, with no text written for the scheduler. */
export type HolidaySms = { on: boolean; message: string };

export const NO_SMS: HolidaySms = { on: false, message: "" };

/** The message to send, or null when texting is off. */
export const smsToSend = (sms: HolidaySms): string | null => (sms.on ? sms.message.trim() : null);

/** Texting is on but nothing is written: the popup asks for a message instead of saving. */
export const smsMissingText = (sms: HolidaySms): boolean => sms.on && sms.message.trim() === "";

type Props = {
	sms: HolidaySms;
	onChange: (sms: HolidaySms) => void;
	/** "Text affected staff", "Text Maria", ... */
	label: string;
	disabled?: boolean;
	dataTestId: string;
};

/** "Text affected staff": one optional message to everyone whose shifts change. */
export const HolidaySmsField: FC<Props> = ({ sms, onChange, label, disabled, dataTestId }) => {
	const { t } = useTranslation();
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<SystemRoundedCheckbox
				checked={sms.on}
				onChange={(on) => onChange({ ...sms, on })}
				label={label}
				disabled={disabled}
				data-testid={`${dataTestId}-toggle`}
			/>
			{sms.on && (
				<SystemValidationTextField
					value={sms.message}
					onChange={(event) => onChange({ ...sms, message: event.target.value })}
					placeholder={t("holidays.sms.placeholder")}
					disabled={disabled}
					fullWidth
					autoFocus
					injectedProps={{ multiline: true, minRows: 3, slotProps: { htmlInput: { "data-testid": `${dataTestId}-message` } } }}
				/>
			)}
		</Box>
	);
};
